import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, Download, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";
import { AuthCard } from "@/components/shell/auth-card";

// "Application" resolves to /wallets — the same post-Auth landing route
// tenant-select's Continue uses elsewhere in this app.
const APPLICATION_ROUTE = "/wallets";

const STEPS = [
  { id: 1, label: "Register" },
  { id: 2, label: "Name Device" },
  { id: 3, label: "Backup Codes" },
  { id: 4, label: "Confirm" },
] as const;

type StepId = (typeof STEPS)[number]["id"];

// No 0/O/1/I — avoids ambiguous characters in a code the user has to
// transcribe or read back later.
const CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
function generateRecoveryCodes(count: number): string[] {
  const segment = () =>
    Array.from({ length: 4 }, () => CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]).join("");
  return Array.from({ length: count }, () => `${segment()}-${segment()}-${segment()}`);
}

// Already-enrolled credentials, shown in the summary table below the
// wizard — matches the canonical LOG-03 Figma frame. Provider-neutral mock
// data (not the Figma/prototype's own "Marcus" placeholder persona).
const EXISTING_DEVICES = [
  { device: "Daniel's iPhone 15", type: "Face ID (Passkey)", registered: "2026-08-20", lastUsed: "2026-09-14" },
  { device: "YubiKey 5C NFC", type: "Hardware Key (FIDO2)", registered: "2026-07-15", lastUsed: "2026-09-12" },
];

export default function PasskeyEnrollmentPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  // Step 1 (the actual WebAuthn "create credential" browser prompt) is
  // already complete by the time this screen is reached — matches the
  // canonical LOG-03 Figma frame's default captured state.
  const [step, setStep] = useState<StepId>(2);
  const [deviceName, setDeviceName] = useState(() => `${(user?.name ?? "My").split(" ")[0]}'s MacBook Pro`);
  const [copied, setCopied] = useState(false);
  const recoveryCodes = useMemo(() => generateRecoveryCodes(10), []);

  function copyCodes() {
    navigator.clipboard?.writeText(recoveryCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function downloadCodes() {
    const blob = new Blob([recoveryCodes.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "alloy-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  function primaryAction() {
    if (step === 4) {
      navigate(APPLICATION_ROUTE);
      return;
    }
    setStep((s) => (s + 1) as StepId);
  }

  // Preserved from the validated prototype (passkey-enrollment.html's
  // "Skip for now — remind me later" → straight to the app).
  function skip() {
    navigate(APPLICATION_ROUTE);
  }

  const primaryLabel = step === 4 ? "Continue to your workspace →" : "Save & Continue";
  const devices = step === 4 ? [{ device: deviceName, type: "Touch ID (Passkey)", registered: "2026-09-17", lastUsed: "Just now" }, ...EXISTING_DEVICES] : EXISTING_DEVICES;

  return (
    <div className="flex w-full flex-col items-center gap-4">
      <AuthCard variant="wide">
        <div className="auth-icon" aria-hidden="true">
          <div className="auth-icon__inner">
            <Mail size={28} strokeWidth={1.25} />
          </div>
        </div>

        <div className="flex w-full flex-col gap-4">
          <div className="auth-heading">
            <h1 className="auth-title">Set Up Your Passkey</h1>
            {user?.email && <p className="auth-subtitle">{user.email}</p>}
          </div>

          <div className="flex w-full items-center justify-between gap-2 rounded-[10px] border border-line bg-white px-4 py-2.5">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2">
                <span
                  className={cn(
                    "grid size-[22px] shrink-0 place-items-center rounded-full text-[11px] font-semibold",
                    s.id < step ? "bg-success text-white" : s.id === step ? "bg-primary text-white" : "bg-disabled-soft text-muted"
                  )}
                >
                  {s.id < step ? <Check className="size-3" aria-hidden /> : s.id}
                </span>
                <span className={cn("text-xs font-semibold whitespace-nowrap", s.id === step ? "text-ink" : s.id < step ? "text-ink" : "text-muted font-normal")}>
                  {s.label}
                </span>
                {i < STEPS.length - 1 && <span className="mx-2 h-px w-7 shrink-0 bg-line" aria-hidden />}
              </div>
            ))}
          </div>

          <div className="flex w-full flex-col gap-2">
            {/* Step 1 — always resolved by the time this page is reached */}
            <div className="flex items-start justify-between gap-3 rounded border border-line bg-surface px-4 py-2.5">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-success text-white">
                  <Check className="size-3" aria-hidden />
                </span>
                <div>
                  <div className="text-sm font-bold text-ink">Register Passkey</div>
                  <div className="text-xs text-muted">
                    Your browser prompted you to create a passkey using Touch ID. The credential has been registered with Alloy&apos;s server.
                  </div>
                </div>
              </div>
              <Badge variant="success" dot={false}>Complete</Badge>
            </div>

            {/* Step 2 */}
            <div className={cn("rounded border px-4 py-2.5", step === 2 ? "border-2 border-primary bg-primary-soft" : "border-line bg-surface")}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span
                    className={cn(
                      "mt-0.5 grid size-5 shrink-0 place-items-center rounded-full text-xs font-bold",
                      step > 2 ? "bg-success text-white" : step === 2 ? "bg-primary text-white" : "bg-disabled-soft text-muted"
                    )}
                  >
                    {step > 2 ? <Check className="size-3" aria-hidden /> : 2}
                  </span>
                  <div className="text-sm font-bold text-ink">Name This Device</div>
                </div>
                {step === 2 && <Badge variant="info" dot={false}>Current</Badge>}
              </div>
              {step === 2 && (
                <div className="mt-2 flex flex-col gap-2 pl-8">
                  <p className="text-xs text-muted">Give this passkey a recognizable name so you can identify it later in your security settings.</p>
                  <label htmlFor="device-name" className="text-xs font-semibold text-ink">Device Name</label>
                  <div className="auth-input">
                    <input id="device-name" value={deviceName} onChange={(e) => setDeviceName(e.target.value)} />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] text-muted">This appears in your registered devices list</p>
                    <button type="button" onClick={skip} className="shrink-0 text-xs font-semibold text-primary outline-none hover:underline">
                      Skip for now — remind me later
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Step 3 */}
            <div className={cn("rounded border px-4 py-2.5", step === 3 ? "border-2 border-primary bg-primary-soft" : "border-line bg-surface")}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span
                    className={cn(
                      "mt-0.5 grid size-5 shrink-0 place-items-center rounded-full text-xs font-bold",
                      step > 3 ? "bg-success text-white" : step === 3 ? "bg-primary text-white" : "bg-disabled-soft text-muted"
                    )}
                  >
                    {step > 3 ? <Check className="size-3" aria-hidden /> : 3}
                  </span>
                  <div className="text-sm font-bold text-ink">Save Recovery Codes</div>
                </div>
                {step === 3 && <Badge variant="info" dot={false}>Current</Badge>}
              </div>
              {step === 3 && (
                <div className="mt-2 flex flex-col gap-3 pl-8">
                  <p className="text-xs text-muted">Download or copy your one-time recovery codes. Each code can be used once if you lose access to your passkey.</p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {recoveryCodes.map((code) => (
                      <span key={code} className="rounded bg-disabled-soft px-2.5 py-1.5 text-center font-mono text-[11px] font-medium text-ink">
                        {code}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={copyCodes}>
                      <Copy className="size-3.5" aria-hidden /> {copied ? "Copied!" : "Copy Codes"}
                    </Button>
                    <Button variant="secondary" size="sm" onClick={downloadCodes}>
                      <Download className="size-3.5" aria-hidden /> Download .txt
                    </Button>
                  </div>
                  <div className="rounded bg-warning-soft px-3 py-2 text-[11px] leading-relaxed text-warning">
                    Store these codes securely. They will not be shown again. Do not share them or store them alongside your device.
                  </div>
                </div>
              )}
            </div>

            {/* Step 4 */}
            <div className={cn("rounded border px-4 py-2.5", step === 4 ? "border-2 border-primary bg-primary-soft" : "border-line bg-surface")}>
              <div className="flex items-start gap-3">
                <span
                  className={cn(
                    "mt-0.5 grid size-5 shrink-0 place-items-center rounded-full text-xs font-bold",
                    step === 4 ? "bg-primary text-white" : "bg-disabled-soft text-muted"
                  )}
                >
                  4
                </span>
                <div>
                  <div className="text-sm font-bold text-ink">Enrollment Complete</div>
                  {step === 4 && (
                    <div className="text-xs text-muted">Your passkey is ready. You can manage enrolled devices anytime in Profile → Security.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex w-full items-center justify-end gap-2">
            {step > 2 && (
              <Button variant="secondary" onClick={() => setStep((s) => (s - 1) as StepId)}>
                Back
              </Button>
            )}
            <Button className={step > 2 ? "" : "w-full"} onClick={primaryAction} disabled={step === 2 && !deviceName.trim()}>
              {primaryLabel}
            </Button>
          </div>
        </div>
      </AuthCard>

      <div className="auth-card auth-card--wide">
        <div className="auth-card__inner">
          <div className="auth-card__content" style={{ gap: 12 }}>
            <h2 className="w-full text-left text-sm font-bold text-ink">Enrolled Devices Summary</h2>
            <div className="w-full overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-line text-muted">
                    <th className="whitespace-nowrap py-2 pr-3 font-medium">Device</th>
                    <th className="whitespace-nowrap py-2 pr-3 font-medium">Type</th>
                    <th className="whitespace-nowrap py-2 pr-3 font-medium">Registered</th>
                    <th className="whitespace-nowrap py-2 pr-3 font-medium">Last Used</th>
                    <th className="whitespace-nowrap py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => (
                    <tr key={d.device} className="border-b border-line last:border-0">
                      <td className="whitespace-nowrap py-2 pr-3 font-semibold text-ink">{d.device}</td>
                      <td className="whitespace-nowrap py-2 pr-3 text-muted">{d.type}</td>
                      <td className="whitespace-nowrap py-2 pr-3 text-muted">{d.registered}</td>
                      <td className="whitespace-nowrap py-2 pr-3 text-muted">{d.lastUsed}</td>
                      <td className="whitespace-nowrap py-2">
                        <Badge variant="success" dot={false} size="sm">Active</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
